import PlannerStartTime from './PlannerStartTime';
import { OfficeInstructions } from "@/components/ScheduleInstructions";
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { EmptyState } from "@/pages/dashboard/_shared";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Coffee, Hammer, MapPin, Check, Plus, X, Clock } from "lucide-react";
import { toast } from "sonner";
import { JobPlans } from "@/components/JobPlans";

const NO_WORK_VALUE = "__no_work__";
const ADD_PLACEHOLDER = "__add__";

// Compact, keyboard-first list view for the Dispatch page. One row per field
// worker, with an inline dropdown to add jobs (or "No work"), plus a
// per-job "Scope of works" textarea and plans/drawings editor that auto-saves.
// A worker can be assigned to MULTIPLE jobs on the same day — each job shows
// as a removable chip with its own scope-of-works editor underneath.
export function DispatchListView({ date, onChanged }) {
  const [workers, setWorkers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [crewInstructions,setCrewInstructions]=useState([]);
  const [savingId, setSavingId] = useState(null);

  const requestRef = useRef(null);
  const loadAll = async () => {
    requestRef.current?.abort();
    const controller = new AbortController(); requestRef.current = controller;
    try {
      const {data} = await api.get('/dispatch', {params:{date},signal:controller.signal});
      if (controller.signal.aborted) return;
      setWorkers(data.users.filter(x=>x.role==='field'));
      setJobs(data.jobs);
      setAssignments(data.assignments);setCrewInstructions(data.crew_instructions || []);
    } catch(e) {if(!controller.signal.aborted) toast.error(e.response?.data?.detail || 'Could not load Day Board');}
  };
  useEffect(() => { loadAll(); return () => requestRef.current?.abort(); /* eslint-disable-next-line */ }, [date]);

  // Map user_id -> array of their assignment rows for this date
  const assignmentsByUser = useMemo(() => {
    const m = {};
    for (const a of assignments) (m[a.user_id] ||= []).push(a);
    return m;
  }, [assignments]);

  // Per-ASSIGNMENT scope-of-works (each worker on a job can have their own
  // instructions). Auto-save via debounced PATCH /assignments/{id}.
  const scopeSaveTimers = useRef({});
  const [scopeSavedAt, setScopeSavedAt] = useState({});
  const updateAssignmentScope = (assignmentId, nextScope) => {
    setAssignments((arr) => arr.map((a) => a.id === assignmentId ? { ...a, scope_of_works: nextScope } : a));
    setScopeSavedAt((s) => ({ ...s, [assignmentId]: "saving" }));
    clearTimeout(scopeSaveTimers.current[assignmentId]);
    scopeSaveTimers.current[assignmentId] = setTimeout(async () => {
      try {
        await api.patch(`/assignments/${assignmentId}`, { scope_of_works: nextScope });
        setScopeSavedAt((s) => ({ ...s, [assignmentId]: "saved" }));
        setTimeout(() => {
          setScopeSavedAt((s) => { const c = { ...s }; delete c[assignmentId]; return c; });
        }, 1500);
      } catch {
        toast.error("Couldn't save scope of works");
        loadAll();
      }
    }, 700);
  };

  // Per-assignment site start time — immediate local update, debounced PATCH
  const updateStartTime = (assignmentId, st) => {
    setAssignments((arr) => arr.map((a) => a.id === assignmentId ? { ...a, start_time: st } : a));
    setScopeSavedAt((s) => ({ ...s, [assignmentId]: "saving" }));
    clearTimeout(scopeSaveTimers.current["st_" + assignmentId]);
    scopeSaveTimers.current["st_" + assignmentId] = setTimeout(async () => {
      try {
        await api.patch(`/assignments/${assignmentId}`, { start_time: st || null });
        setScopeSavedAt((s) => ({ ...s, [assignmentId]: "saved" }));
        setTimeout(() => {
          setScopeSavedAt((s) => { const c = { ...s }; delete c[assignmentId]; return c; });
        }, 1500);
      } catch {
        toast.error("Couldn't save start time");
        loadAll();
      }
    }, 500);
  };

  const handleAdd = async (worker, value) => {
    if (value === ADD_PLACEHOLDER) return;
    const job_id = value === NO_WORK_VALUE ? null : value;
    setSavingId(worker.id);
    try {
      const result = await api.post("/crew-planner/assign", { user_ids: [worker.id], dates: [date], job_id });
      (result.data.warnings || []).slice(0, 5).forEach(w=>toast.warning(w));
      await loadAll();
      onChanged?.();
      const firstName = worker.name.split(" ")[0];
      toast.success(
        job_id
          ? `${firstName} → ${jobs.find((j) => j.id === job_id)?.title || "job"}`
          : `${firstName} → Day off`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const removeAssignment = async (assignmentId) => {
    try {
      await api.delete(`/dispatch/assignment/${assignmentId}`);
      await loadAll();
      onChanged?.();
    } catch {
      toast.error("Remove failed");
    }
  };

  if (workers.length === 0) {
    return (
      <EmptyState
        title="No field workers"
        description="Add field workers from the Team page before assigning them."
      />
    );
  }

  return (
    <div data-testid="dispatch-list-view">
      <div className="space-y-2">
        {workers.map((w) => {
          const rows = assignmentsByUser[w.id] || [];
          const jobRows = rows.filter((a) => a.job_id);
          const dayOffRow = rows.find((a) => a.job_id === null);
          const isSaving = savingId === w.id;
          // Hide jobs the worker is already on from the "+ Add" dropdown.
          const alreadyJobIds = new Set(jobRows.map((a) => a.job_id));
          const availableJobs = jobs.filter((j) => !alreadyJobIds.has(j.id));
          return (
            <div key={w.id} className="hard-card p-4 space-y-3" data-testid={`worker-row-${w.id}`}>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-start">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-10 h-10 bg-zinc-900 text-white rounded-sm flex items-center justify-center font-bold text-sm shrink-0">
                    {w.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold uppercase truncate">{w.name}</div>
                    {jobRows.length > 0 ? (
                      <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`worker-jobs-${w.id}`}>
                        {jobRows.map((a) => (
                          <span
                            key={a.id}
                            className="inline-flex items-center gap-1 bg-orange-50 dark:bg-[#FF5F15]/20 border border-[#FF5F15] text-orange-900 dark:text-orange-100 text-[11px] font-bold uppercase tracking-wider pl-2 pr-1 py-0.5 rounded-sm"
                            data-testid={`worker-job-chip-${w.id}-${a.job_id}`}
                          >
                            <Hammer className="w-3 h-3 text-[#FF5F15]" />
                            <span className="normal-case">{a.job_title}</span>
                            {a.start_time && (
                              <span className="inline-flex items-center gap-0.5 font-mono text-[11px] text-zinc-600 dark:text-zinc-300">
                                <Clock className="w-2.5 h-2.5" />{a.start_time}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => removeAssignment(a.id)}
                              className="ml-0.5 text-zinc-400 hover:text-red-600 transition-colors"
                              data-testid={`worker-job-remove-${w.id}-${a.job_id}`}
                              aria-label={`Remove ${a.job_title}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : dayOffRow ? (
                      <div className="mt-1 text-xs text-zinc-500 inline-flex items-center gap-1">
                        <Coffee className="w-3 h-3" /> Day off
                        <button
                          type="button"
                          onClick={() => removeAssignment(dayOffRow.id)}
                          className="ml-1 text-zinc-400 hover:text-red-600"
                          aria-label="Clear day off"
                          data-testid={`worker-clear-dayoff-${w.id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="mt-1 text-xs text-zinc-400 italic">Not assigned yet</div>
                    )}
                  </div>
                </div>

                <div className="sm:w-72">
                  <Select value={ADD_PLACEHOLDER} onValueChange={(v) => handleAdd(w, v)} disabled={isSaving}>
                    <SelectTrigger
                      className={`rounded-sm border-zinc-400 ${jobRows.length > 0 ? "border-[#FF5F15] bg-orange-50 dark:bg-[#FF5F15]/10" : ""}`}
                      data-testid={`assign-select-${w.id}`}>
                      <SelectValue placeholder={jobRows.length > 0 ? "+ Add another job…" : "Assign a job…"}>
                        <span className="inline-flex items-center gap-1.5 text-zinc-700 dark:text-orange-100">
                          <Plus className="w-3.5 h-3.5" />
                          {jobRows.length > 0 ? "Add another job…" : "Assign a job…"}
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_WORK_VALUE} data-testid={`assign-no-work-${w.id}`}>
                        <div className="flex items-center gap-2 text-zinc-700">
                          <Coffee className="w-3.5 h-3.5" /> No work (day off)
                        </div>
                      </SelectItem>
                      {availableJobs.length > 0 && <div className="my-1 border-t border-zinc-200" />}
                      {availableJobs.map((j) => (
                        <SelectItem key={j.id} value={j.id} data-testid={`assign-job-${w.id}-${j.id}`}>
                          <div className="flex items-center gap-2">
                            <Hammer className="w-3.5 h-3.5 text-[#FF5F15]" />
                            <span className="font-medium">{j.title}</span>
                            {j.client_name && (
                              <span className="text-xs text-zinc-500">· {j.client_name}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                      {availableJobs.length === 0 && jobRows.length > 0 && (
                        <div className="px-2 py-1.5 text-xs text-zinc-400 italic">All jobs already added</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {jobRows.map((a) => {
                const job = jobs.find((j) => j.id === a.job_id);
                if (!job) return null;
                const scope = a.scope_of_works || "";
                return (
                  <div key={a.id} className="ml-13 sm:ml-[52px] border-l-2 border-[#FF5F15] bg-orange-50/50 dark:bg-[#FF5F15]/5 p-3" data-testid={`scope-${w.id}-${job.id}`}>
                    <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                      <div className="label-tag !text-[#FF5F15]">
                        Personal instruction for {w.name.split(" ")[0]} · {job.title}
                      </div>
                      <div className="flex items-center gap-2">
                        <PlannerStartTime
                          value={a.start_time || ''}
                          onChange={time => updateStartTime(a.id, time)}
                          label={`Start time for ${w.name}`}
                          testId={`start-time-input-${w.id}-${job.id}`}
                        />
                        <SaveBadge state={scopeSavedAt[a.id]} />
                      </div>
                    </div>
                    <OfficeInstructions crew={crewInstructions.find(n=>n.job_id===job.id)}/>
                    <p className="text-xs font-semibold mb-1">Personal instruction · {w.name} only (and office)</p>
                    <Textarea
                      rows={Math.max(3, scope.split("\n").length + 1)}
                      value={scope}
                      onChange={(e) => updateAssignmentScope(a.id, e.target.value)}
                      placeholder={"What does this worker need to do on this job today?\nE.g.\n• Excavate 18m trench (Ben)\n• Compaction & install Ø450 RCRRJ culvert (Mara)"}
                      className="rounded-sm border-zinc-300 bg-white dark:bg-[#0B1018] dark:border-zinc-700 dark:text-zinc-100 font-mono text-xs leading-relaxed"
                      data-testid={`scope-input-${w.id}-${job.id}`}
                    />
                    <div className="mt-1 text-[11px] font-mono uppercase tracking-widest text-zinc-400 dark:text-zinc-500">
                      Just for {w.name.split(" ")[0]} · auto-saves · others on this job have their own scope
                    </div>
                    <div className="mt-3">
                      <div className="label-tag !text-[#FF5F15] mb-1.5">Plans & drawings (shared on this job)</div>
                      <JobPlans
                        jobId={job.id}
                        plans={job.plans || []}
                        editable={true}
                        compact={true}
                        onChange={(next) => setJobs((arr) => arr.map((jj) => jj.id === job.id ? { ...jj, plans: next } : jj))}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="mt-6 text-xs text-zinc-500 text-center font-mono uppercase tracking-widest" data-testid="dispatch-list-footer">
        Changes save automatically · {workers.length} field worker{workers.length === 1 ? "" : "s"} · workers can be split across multiple jobs
      </div>
    </div>
  );
}

function SaveBadge({ state }) {
  if (state === "saving") {
    return <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Saving…</span>;
  }
  if (state === "saved") {
    return (
      <span className="text-[11px] font-bold uppercase tracking-widest text-green-700 inline-flex items-center gap-1">
        <Check className="w-3 h-3" /> Saved
      </span>
    );
  }
  return null;
}
